import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Search } from 'lucide-react';
import { LibrarySearch } from './LibrarySearch';
import type { LibraryElement } from './LibraryElement';
import { NodeLibraryCategoryItem } from './NodeLibraryCategoryItem';
interface Category {
    name: string;
    color: string;
}

interface Props {
    library: LibraryElement[];
    onElementHover: (element: LibraryElement) => void;
    onElementSelect: (element: LibraryElement) => void;
    selectedElement: LibraryElement | undefined;
}

export const NodeLibraryPanel: React.FC<Props> = ({ library, onElementHover, onElementSelect, selectedElement }) => {
    const [searchValue, setSearchValue] = useState('');
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const librarySearch = useMemo(() => new LibrarySearch(library), [library]);
    const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
    const [keyboardNavActive, setKeyboardNavActive] = useState(false);
    const isSearching = searchValue.length > 0;

    useEffect(() => {
        searchInputRef.current?.focus();
    }, []);

    const [filteredElements, setFilteredElements] = useState<LibraryElement[]>([]);
    useEffect(() => {
        const searchResult = librarySearch.searchLibraryElementsMemoized(searchValue);
        setFilteredElements(searchResult);
        if (searchResult.length > 0) {
            onElementHover(searchResult[0]);
        }
    }, [searchValue, librarySearch, onElementHover]);

    const categories: Category[] = useMemo(() => {
        const dictionary: { [key: string]: string } = {};
        for (const element of filteredElements) {
            dictionary[element.category] = element.color;
        }
        return Object.entries(dictionary).map(([name, color]) => ({ name, color }));
    }, [filteredElements]);

    const filteredElementsByCategory = useMemo(() => {
        const categoryMap = new Map<string, LibraryElement[]>();
        for (const category of categories) {
            categoryMap.set(category.name, []);
        }
        for (const element of filteredElements) {
            categoryMap.get(element.category)?.push(element);
        }
        return categoryMap;
    }, [filteredElements, categories]);

    // Flat list of visible (in open categories or searching) nodes for keyboard navigation
    const visibleNodes = useMemo(() => {
        const nodes: LibraryElement[] = [];
        for (const category of categories) {
            if (isSearching || openCategories.has(category.name)) {
                const categoryNodes = filteredElementsByCategory.get(category.name) ?? [];
                nodes.push(...categoryNodes);
            }
        }
        return nodes;
    }, [categories, filteredElementsByCategory, openCategories, isSearching]);

    const handleNodeClick = useCallback(() => {
        if (selectedElement) {
            onElementSelect(selectedElement);
        }
    }, [selectedElement, onElementSelect]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            switch (event.key) {
                case 'Enter': {
                    event.preventDefault();
                    if (selectedElement) {
                        onElementSelect(selectedElement);
                    }
                    break;
                }
                case 'ArrowUp': {
                    event.preventDefault();
                    setKeyboardNavActive(true);
                    const currentIndex = visibleNodes.findIndex(node => node === selectedElement);
                    const newIndex = currentIndex <= 0 ? visibleNodes.length - 1 : currentIndex - 1;
                    if (visibleNodes[newIndex]) {
                        onElementHover(visibleNodes[newIndex]);
                    }
                    break;
                }
                case 'ArrowDown': {
                    event.preventDefault();
                    setKeyboardNavActive(true);
                    const currentIndex = visibleNodes.findIndex(node => node === selectedElement);
                    const newIndex = currentIndex >= visibleNodes.length - 1 ? 0 : currentIndex + 1;
                    if (visibleNodes[newIndex]) {
                        onElementHover(visibleNodes[newIndex]);
                    }
                    break;
                }
            }
        },
        [selectedElement, visibleNodes, onElementSelect, onElementHover],
    );

    const handleElementHover = useCallback(
        (element: LibraryElement) => {
            setKeyboardNavActive(false);
            onElementHover(element);
        },
        [onElementHover],
    );

    const toggleCategory = useCallback((categoryName: string) => {
        setOpenCategories(previous => {
            const next = new Set(previous);
            if (next.has(categoryName)) {
                next.delete(categoryName);
            } else {
                next.add(categoryName);
            }
            return next;
        });
    }, []);

    return (
        <div
            className="relative flex flex-col h-full backdrop-blur-xl bg-white/70 dark:bg-black/50 border border-black/10 dark:border-white/10 rounded-xl overflow-hidden animate-in fade-in slide-in-from-right-2 duration-150"
            onKeyDown={handleKeyDown}
            onWheel={event => event.stopPropagation()}
        >
            <div className="flex items-center gap-2 px-3 py-3">
                <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input
                    ref={searchInputRef}
                    value={searchValue}
                    onChange={event => setSearchValue(event.target.value)}
                    placeholder="Search blocks..."
                    aria-label="Search for blocks"
                    className="h-7 border-0 bg-transparent shadow-none focus-visible:ring-0 p-0 text-sm"
                />
            </div>
            <Separator className="bg-black/10 dark:bg-white/10" />
            <ScrollArea className="flex-1">
                <div className="py-1">
                    {categories.map(category => (
                        <NodeLibraryCategoryItem
                            key={category.name}
                            categoryName={category.name}
                            categoryColor={category.color}
                            nodes={filteredElementsByCategory.get(category.name) ?? []}
                            selectedElement={selectedElement}
                            isOpen={isSearching || openCategories.has(category.name)}
                            onToggle={() => toggleCategory(category.name)}
                            onElementHover={handleElementHover}
                            onElementClick={handleNodeClick}
                            scrollSelectedIntoView={keyboardNavActive}
                        />
                    ))}
                </div>
            </ScrollArea>
        </div>
    );
};
